import { createContext, useContext, useMemo } from 'react';
import { Outlet, useMatches } from 'react-router';

/**
 * Light and dark theme of the new design (SP-UX10.1). The choice lives in the browser under the
 * same key as on the landing (`sp-theme`, no cookie, no column in the database); without one the
 * system decides. `/theme.js` applies it before the first paint (CSP: a file, no inline script).
 */
export type ThemeChoice = 'light' | 'dark';

const KEY = 'sp-theme';

export function savedTheme(): ThemeChoice | null {
  try {
    const value = localStorage.getItem(KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null; // storage blocked: the system theme
  }
}

export function saveTheme(choice: ThemeChoice) {
  try {
    localStorage.setItem(KEY, choice);
  } catch {
    // storage blocked: the choice lasts for this page only
  }
}

/** Puts the theme on <html>: the choice, or none — then the system's. */
export function applyTheme(choice: ThemeChoice | null = savedTheme()) {
  const root = document.documentElement;
  if (choice) root.setAttribute('data-theme', choice);
  else root.removeAttribute('data-theme');
}

/** The theme a visitor sees right now (for the switch: the other one comes next). */
export function shownTheme(): ThemeChoice {
  const set = document.documentElement.getAttribute('data-theme');
  if (set === 'light' || set === 'dark') return set;
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

/**
 * The frame of a site page (router.tsx, SP-UX10.3): `wide` on the ones laid out over the whole
 * width with the side panel below (site-findings.html).
 */
export interface SiteFrame {
  readonly wide: boolean;
}
const Frame = createContext<SiteFrame>({ wide: false });

export const useSiteFrame = () => useContext(Frame);

type Handle = Partial<Record<'wide', boolean>> | undefined;

/** Root of the router: tells the site frame whether the screen shown is laid out wide. */
export function FrameScope() {
  const matches = useMatches();
  const wide = matches.some((match) => (match.handle as Handle)?.wide === true);
  return (
    <Frame.Provider value={useMemo(() => ({ wide }), [wide])}>
      <Outlet />
    </Frame.Provider>
  );
}
